import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const DATA_MANAGER_FROZEN_PANES_STORAGE_KEY = "grain-saas:data-manager:frozen-panes:v2";
const LEGACY_DATA_MANAGER_FROZEN_PANES_STORAGE_KEY = "grain-saas:data-manager:frozen-panes:v1";

const memoryFrozenPanesSnapshot = {};

function getWindowSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return userUiPreferenceStorage("session");
  } catch {
    return null;
  }
}

function resolveFrozenPanesStorage(storage = null) {
  if (
    storage
    && typeof storage.getItem === "function"
    && typeof storage.setItem === "function"
    && typeof storage.removeItem === "function"
  ) {
    return storage;
  }

  return getWindowSessionStorage();
}

function parseStorageSnapshot(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue)
      ? parsedValue
      : null;
  } catch {
    return null;
  }
}

function normalizeFrozenColumnKeysArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalizedKeys = [];
  const seenKeys = new Set();

  value.forEach((columnKey) => {
    const normalizedKey = typeof columnKey === "string" ? columnKey.trim() : "";
    if (!normalizedKey || seenKeys.has(normalizedKey)) {
      return;
    }

    seenKeys.add(normalizedKey);
    normalizedKeys.push(normalizedKey);
  });

  return normalizedKeys;
}

function readFrozenPanesSnapshot(storage = null) {
  const resolvedStorage = resolveFrozenPanesStorage(storage);

  if (resolvedStorage) {
    try {
      const rawSnapshot = resolvedStorage.getItem(DATA_MANAGER_FROZEN_PANES_STORAGE_KEY);
      const parsedSnapshot = parseStorageSnapshot(rawSnapshot);
      if (parsedSnapshot) {
        return parsedSnapshot;
      }
    } catch {
      return { ...memoryFrozenPanesSnapshot };
    }
  }

  return { ...memoryFrozenPanesSnapshot };
}

function writeFrozenPanesSnapshot(snapshot, storage = null) {
  const resolvedStorage = resolveFrozenPanesStorage(storage);
  Object.keys(memoryFrozenPanesSnapshot).forEach((tableKey) => {
    delete memoryFrozenPanesSnapshot[tableKey];
  });
  Object.assign(memoryFrozenPanesSnapshot, snapshot);

  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.setItem(DATA_MANAGER_FROZEN_PANES_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage is best-effort only.
  }
}

function readLegacyFrozenUpToColumnKey(tableKey, storage = null) {
  const resolvedStorage = resolveFrozenPanesStorage(storage);
  if (!resolvedStorage) {
    return null;
  }

  try {
    const rawSnapshot = resolvedStorage.getItem(LEGACY_DATA_MANAGER_FROZEN_PANES_STORAGE_KEY);
    const parsedSnapshot = parseStorageSnapshot(rawSnapshot);
    const legacyValue = parsedSnapshot?.[tableKey];
    return typeof legacyValue === "string" && legacyValue.trim() ? legacyValue.trim() : null;
  } catch {
    return null;
  }
}

export function readDataManagerFrozenPanesState(tableKey, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return [];
  }

  const snapshot = readFrozenPanesSnapshot(storage);
  const storedKeys = normalizeFrozenColumnKeysArray(snapshot[normalizedTableKey]);
  if (storedKeys.length > 0) {
    return storedKeys;
  }

  const legacyFrozenUpToColumnKey = readLegacyFrozenUpToColumnKey(normalizedTableKey, storage);
  return legacyFrozenUpToColumnKey ? [legacyFrozenUpToColumnKey] : [];
}

export function resolveInitialDataManagerFrozenPanesState(
  tableKey,
  savedFrozenColumnKeys = null,
  storage = null,
) {
  const normalizedSavedKeys = normalizeFrozenColumnKeysArray(savedFrozenColumnKeys);
  return normalizedSavedKeys.length > 0
    ? normalizedSavedKeys
    : readDataManagerFrozenPanesState(tableKey, storage);
}

export function shouldMigrateDataManagerFrozenPanesState(
  savedFrozenColumnKeys = null,
  legacyFrozenColumnKeys = [],
) {
  return Array.isArray(savedFrozenColumnKeys)
    && normalizeFrozenColumnKeysArray(savedFrozenColumnKeys).length === 0
    && normalizeFrozenColumnKeysArray(legacyFrozenColumnKeys).length > 0;
}

export function persistDataManagerFrozenPanesState(tableKey, frozenColumnKeys, storage = null) {
  const normalizedTableKey = typeof tableKey === "string" ? tableKey.trim() : "";
  if (!normalizedTableKey) {
    return [];
  }

  const normalizedFrozenColumnKeys = normalizeFrozenColumnKeysArray(frozenColumnKeys);
  const nextSnapshot = {
    ...readFrozenPanesSnapshot(storage),
    [normalizedTableKey]: normalizedFrozenColumnKeys,
  };

  if (normalizedFrozenColumnKeys.length === 0) {
    delete nextSnapshot[normalizedTableKey];
  }

  writeFrozenPanesSnapshot(nextSnapshot, storage);
  return normalizedFrozenColumnKeys;
}

export function clearDataManagerFrozenPanesState(tableKey = "", storage = null) {
  persistDataManagerFrozenPanesState(tableKey, [], storage);
}

export { DATA_MANAGER_FROZEN_PANES_STORAGE_KEY };
